import React, { useRef, useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import TestimonialCard from "../../../components/common/TestimonialCard";
import { Images } from "../../../utils/Images";

const LIFE_TESTIMONIALS_CMS_SECTION_ID = 98;

const Testimonials = () => {
  const theme = useTheme();
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const home = useSelector((state) => state.cms.home);
  const { sectionTitle, testimonialData } = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === LIFE_TESTIMONIALS_CMS_SECTION_ID);
    if (!section || section.status === 0) {
      return { sectionTitle: "", testimonialData: [] };
    }
    const related = Array.isArray(section.relatedCMS) ? section.relatedCMS.filter((r) => r?.status === 1) : [];
    const testimonials = related.map((item) => ({
      id: item.id,
      name: item.title ?? "",
      description: item.sub_title ?? "",
      image: item.image ?? null,
    }));
    return {
      sectionTitle: section.title ?? "",
      testimonialData: testimonials,
    };
  }, [home]);

  // Responsive breakpoints (match Home page TestimonialSection)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.between("md", "lg"));

  // Dynamic card width and gap based on screen size (match Home)
  const getCardDimensions = () => {
    if (isMobile) {
      return { cardWidth: 260, gap: 16, cardsPerView: 1 };
    } else if (isTablet) {
      // 600–900px: two cards visible
      return { cardWidth: 300, gap: 20, cardsPerView: 2 };
    } else if (isDesktop) {
      // 900–1200px: three cards visible
      return { cardWidth: 300, gap: 24, cardsPerView: 3 };
    } else {
      // ≥1200px (incl. 1440+): four cards visible — matches the homepage
      return { cardWidth: 300, gap: 24, cardsPerView: 4 };
    }
  };

  const { cardWidth, gap, cardsPerView } = getCardDimensions();

  // Calculate maximum possible index
  const maxIndex = Math.max(
    0,
    testimonialData.length - Math.floor(cardsPerView)
  );

  // Update navigation states based on current index
  const updateNavigationStates = () => {
    setCanScrollLeft(currentIndex > 0);
    setCanScrollRight(currentIndex < maxIndex);
  };

  useEffect(() => {
    updateNavigationStates();
  }, [currentIndex, maxIndex]);

  // Smooth scroll to specific position
  const scrollToPosition = (index) => {
    if (scrollContainerRef.current) {
      const scrollPosition = index * (cardWidth + gap);
      scrollContainerRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  };

  const handleNext = () => {
    if (currentIndex < maxIndex) {
      const nextIndex = Math.min(currentIndex + 1, maxIndex);
      setCurrentIndex(nextIndex);
      scrollToPosition(nextIndex);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const prevIndex = Math.max(currentIndex - 1, 0);
      setCurrentIndex(prevIndex);
      scrollToPosition(prevIndex);
    }
  };

  // Handle resize to recalculate positions
  useEffect(() => {
    const handleResize = () => {
      setCurrentIndex(0);
      scrollToPosition(0);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [cardWidth, gap]);

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "94.44%",
        maxWidth: "1440px",
        borderRadius: isMobile ? "16px" : "36px",
        backgroundColor: "#E8E8E85C",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        mb: isMobile ? "30px" : "40px",
        mt: isMobile ? "30px" : "50px",
        padding: isMobile ? "30px 0px 20px 16px" : "50px 30px 40px 30px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header: title on left, nav buttons on right (desktop/tablet only) */}
      <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", minWidth: 0 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: isMobile ? "10px" : "13px",
              color: "#131313",
              fontWeight: 400,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              mb: isMobile ? "10px" : "23px",
              display: "flex",
              alignItems: "start",
              justifyContent: "start",
              gap: 1,
            }}
          >
            <Box component="span" sx={{ color: "#A9C74E", fontSize: "20px", lineHeight: 1 }}>
              ●
            </Box>
            Testimonials
          </Typography>

          <Typography
            sx={{
              fontFamily: "Red Hat Display",
              fontWeight: 800,
              fontStyle: isMobile ? "ExtraBold" : "normal",
              fontSize: isMobile ? "18px" : isTablet ? "28px" : "36px",
              lineHeight: isMobile ? "28px" : isTablet ? "36px" : "46px",
              letterSpacing: "0%",
              textAlign: "left",
              verticalAlign: "middle",
              textTransform: "capitalize",
              color: "#1D1D1B",
              marginBottom: isMobile ? "20px" : "60px",
              maxWidth: isMobile ? "100%" : isTablet ? "500px" : "946px",
              marginLeft: "0",
              wordWrap: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {sectionTitle}
          </Typography>
        </Box>

        {/* Nav buttons next to title - desktop/tablet only */}
        <Box sx={{ display: isMobile ? "none" : "flex", width: "120px", gap: "20px", justifyContent: "center", alignItems: "center" }}>
          <IconButton
            onClick={handlePrevious}
            disabled={!canScrollLeft}
            sx={{
              width: "50px",
              height: "50px",
              borderRadius: "50px",
              backgroundColor: canScrollLeft ? "#1D1D1B" : "#B9B9B8",
              border: "none",
              cursor: canScrollLeft ? "pointer" : "not-allowed",
              "&:hover": { backgroundColor: canScrollLeft ? "#2a2a2a" : "#B9B9B8" },
              "&:disabled": { backgroundColor: "#B9B9B8" },
            }}
          >
            <Box component="img" src={Images.leftArrow} alt="Previous" sx={{ width: "15px", height: "12px", filter: canScrollLeft ? "brightness(0) invert(1)" : "none" }} />
          </IconButton>
          <IconButton
            onClick={handleNext}
            disabled={!canScrollRight}
            sx={{
              width: "50px",
              height: "50px",
              borderRadius: "50px",
              backgroundColor: canScrollRight ? "#1D1D1B" : "#B9B9B8",
              border: "none",
              cursor: canScrollRight ? "pointer" : "not-allowed",
              "&:hover": { backgroundColor: canScrollRight ? "#2a2a2a" : "#B9B9B8" },
              "&:disabled": { backgroundColor: "#B9B9B8" },
            }}
          >
            <Box component="img" src={Images.rightArrow} alt="Next" sx={{ width: "15px", height: "12px", filter: canScrollRight ? "brightness(0) invert(1)" : "none" }} />
          </IconButton>
        </Box>
      </Box>

      {/* Testimonial Cards Container */}
      <Box sx={{ width: "100%", minHeight: isMobile ? "274px" : "334px", overflow: "hidden", position: "relative" }}>
        <Box
          ref={scrollContainerRef}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: `${gap}px`,
            minHeight: isMobile ? "274px" : "334px",
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
            padding: "10px 0",
            paddingLeft: "16px",
            paddingRight: "16px",
            scrollSnapType: "x mandatory",
          }}
        >
          {testimonialData.length > 0 ? (
            testimonialData.map((testimonial) => (
              <Box key={testimonial.id} sx={{ flexShrink: 0, width: `${cardWidth}px`, scrollSnapAlign: "start" }}>
                <TestimonialCard testimonial={testimonial} cardWidth={cardWidth} isMobile={isMobile} />
              </Box>
            ))
          ) : (
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", padding: "40px", color: "#7D7D7D" }}>
              <Typography>No testimonials available yet.</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Nav buttons below cards - mobile only */}
      <Box sx={{ display: isMobile ? "flex" : "none", width: "100%", gap: "10px", justifyContent: "center", alignItems: "center", marginTop: "20px" }}>
        <IconButton
          onClick={handlePrevious}
          disabled={!canScrollLeft}
          sx={{
            width: "25px",
            height: "25px",
            borderRadius: "50px",
            backgroundColor: canScrollLeft ? "#1D1D1B" : "#B9B9B8",
            border: "none",
            cursor: canScrollLeft ? "pointer" : "not-allowed",
            "&:hover": { backgroundColor: canScrollLeft ? "#2a2a2a" : "#B9B9B8" },
            "&:disabled": { backgroundColor: "#B9B9B8" },
          }}
        >
          <Box component="img" src={Images.leftArrow} alt="Previous" sx={{ width: "8px", height: "6px", filter: canScrollLeft ? "brightness(0) invert(1)" : "none" }} />
        </IconButton>
        <IconButton
          onClick={handleNext}
          disabled={!canScrollRight}
          sx={{
            width: "25px",
            height: "25px",
            borderRadius: "50px",
            backgroundColor: canScrollRight ? "#1D1D1B" : "#B9B9B8",
            border: "none",
            cursor: canScrollRight ? "pointer" : "not-allowed",
            "&:hover": { backgroundColor: canScrollRight ? "#2a2a2a" : "#B9B9B8" },
            "&:disabled": { backgroundColor: "#B9B9B8" },
          }}
        >
          <Box component="img" src={Images.rightArrow} alt="Next" sx={{ width: "8px", height: "6px", filter: canScrollRight ? "brightness(0) invert(1)" : "none" }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Testimonials;

